import React, {useContext, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import Circular, {LARGE} from '../../../shared/library/indicators/indeterminate/circular';
import {SigninDesktopModal} from '../../../shared/library/modals/signin';
import {PAGE_WIDTH} from '../../../shared/style/dimensions';
import {BLACK, PAGE_BACKGROUND, FOCUS_BLUE, WHITE} from '../../../shared/style/colors';
import Banner from '../shared/styles';
import Sidebar from '../components/sidebar';
import Navigation from '../components/navigation';
import Sections from '../components/sections/index';
import ComposerProvider from '../../../shared/library/composer/state/provider';
import Composer from '../../../shared/library/composer';
import loadable from '@loadable/component';
import {DecisionModal} from '../../feed/components/decision-card/decision-modal';
import {NavigationContext, RouteContext} from '../../../shared/enhancers/router-enhancer';
import {Query} from 'react-apollo';
import PlanModal from '../components/plan-modal';
import {hasFeature} from '../../../shared/utils/has-feature';
import {FEATURE_PREMIUM_COMPANY_PROFILE} from '../../../shared/constants/features';
import Claim from '../components/claim';
import {OwnerContext} from '../enhancers/company-profile';
import {IsAdminContext} from '../enhancers/is-admin';
import {PrivateModeContext} from '../../../shared/enhancers/private-mode-enchancer';
import {CurrentUserContext} from '../../../shared/enhancers/current-user-enhancer';
import {isPrivateMember} from '../../../shared/utils/is-private-member';
import {GhostButton} from '../../team-profile/components/sidebar';
import PrivateStackshareIcon from '../../../shared/library/icons/private-stackshare-small-icon.svg';
import PopoverWithAnchor from '../../../shared/library/popovers/base-v2';
import {BOTTOM_END} from '../../../shared/constants/placements';
import {ALPHA} from '../../../shared/style/color-utils';
import ProgressBar from '../../../shared/library/progress-bar';
import {decisionQuery} from '../../../data/shared/queries';
import {PLANS} from '../../private-payment/constants';
import SsoGithubCtaBanner from '../../../shared/library/cards/sso-github/banner';
import {ApolloContext} from '../../../shared/enhancers/graphql-enhancer';
import StackShareEnterpriseForm from '../../../shared/library/stackshare-enterprise-form';
import {
  ORG_STRUCTURE_MODES,
  OrganizationContext
} from '../../../shared/enhancers/organization-enhancer';
import {isEnterpriseCompany} from '../../../shared/utils/plan-permission-check';

const InstallGithub = loadable(() =>
  import('../../../../app/bundles/site/components/install-github')
);

const Spinner = glamorous.div({
  margin: '100px 0',
  display: 'flex',
  justifyContent: 'center'
});

const Page = glamorous.div({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: PAGE_BACKGROUND,
  minHeight: '100vh',
  position: 'relative'
});

const Main = glamorous.main({
  gridArea: 'content',
  display: 'flex',
  flexDirection: 'column',
  padding: '30px 30px 130px 30px'
});

const Grid = glamorous.div({
  position: 'relative',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: `auto 312px ${PAGE_WIDTH - 312}px auto`,
  gridTemplateRows: '95px auto',
  gridTemplateAreas: `
    ". sidebar nav     nav"
    ". sidebar content .  "
  `
});

const CompanyDashboardButton = glamorous(GhostButton)({
  height: 36,
  fontSize: 13,
  marginBottom: 0,
  marginRight: 0,
  padding: '12px 0',
  '> svg': {
    marginRight: 7,
    height: 20,
    width: 'auto'
  }
});

const PopoverContainer = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  marginRight: 183
});

const CompanyProfile = ({company, loading, canEdit, hasOwner}) => {
  const {signin, decision, showPlan, planSuccess} = useContext(RouteContext);
  const navigate = useContext(NavigationContext);
  const currentUserIsOwner = useContext(OwnerContext);
  const privateMode = useContext(PrivateModeContext);
  const currentUser = useContext(CurrentUserContext);
  const client = useContext(ApolloContext);
  const isAdmin = useContext(IsAdminContext);
  const {orgStructureMode} = useContext(OrganizationContext);
  const [showPssForm, setShowPssForm] = useState(false);
  const currentUserRole = (companies = [], companyId) => {
    const index = companies.findIndex(company => company.id === companyId);
    if (index === -1) return '';
    return companies[index].myRole;
  };
  const showSsoGithubCta = currentUser && currentUser.shouldForceVcsConnection;
  const forcedVcsProvider = privateMode && privateMode.forcedVcsProvider;
  const isUserImpersonated = currentUser && currentUser.impersonated;
  const isVcsOrgs = orgStructureMode === ORG_STRUCTURE_MODES.vcs;

  useEffect(() => {
    if (company && localStorage.getItem('free-plan-selected')) {
      let isFreePlan = Boolean(company.plans.some(plan => plan.slug === PLANS.FREE));
      if (isFreePlan) {
        setShowPssForm(true);
        localStorage.removeItem('free-plan-selected');
      }
    } else {
      setShowPssForm(false);
    }
  }, [company]);

  if (loading) {
    return (
      <Spinner>
        <Circular size={LARGE} />
      </Spinner>
    );
  }

  if (company) {
    const isPrivate = isPrivateMember(company.id, privateMode);
    const exitModal = () => navigate(company.path, true);
    return (
      <>
        <Page>
          {hasFeature(company, FEATURE_PREMIUM_COMPANY_PROFILE) && company.bannerImageUrl && (
            <Banner url={company.bannerImageUrl} />
          )}
          <Grid>
            <Sidebar
              isSiteAdmin={currentUser && currentUser.amIAdmin}
              isAdmin={isAdmin}
              company={company}
              menu={{canEdit, hasOwner}}
            />
            <Navigation company={company}>
              {currentUser && (currentUser.amIAdmin || company.privateMode) && (
                <PopoverContainer>
                  <PopoverWithAnchor
                    customStyle={{
                      backgroundColor: ALPHA(BLACK, 0.6),
                      color: WHITE,
                      textAlign: 'left',
                      padding: '10px 17px',
                      borderRadius: 4
                    }}
                    arrowColor={ALPHA(BLACK, 0.6)}
                    padding={10}
                    placement={BOTTOM_END}
                    activateMode="hover"
                    hidden={true}
                    anchor={
                      <>
                        {currentUser &&
                          currentUserRole(
                            currentUser && currentUser.companies ? currentUser.companies : [],
                            company && company.id
                          ) &&
                          isEnterpriseCompany(company) && (
                            <CompanyDashboardButton
                              href={`/companies/${company.slug}/dashboard`}
                              color={FOCUS_BLUE}
                            >
                              <PrivateStackshareIcon />
                              Dashboard
                            </CompanyDashboardButton>
                          )}
                      </>
                    }
                  >
                    Visit your Company Dashboard to see a summary view of all your stacks and teams
                  </PopoverWithAnchor>
                </PopoverContainer>
              )}
            </Navigation>
            <Main>
              {showSsoGithubCta && (
                <SsoGithubCtaBanner client={client} forcedVcsProvider={forcedVcsProvider} />
              )}
              {company && (company.githubAppInstalled || company.azureAppInstalled) && (
                <ProgressBar />
              )}
              {/* {(isPrivate || (currentUser && currentUser.amIAdmin && company.privateMode)) && (
                <PrivateOnboardingChecklist />
              )} */}
              {currentUser && isVcsOrgs && (
                <InstallGithub company={company} currentUser={currentUser} isPrivate={isPrivate} />
              )}
              {!canEdit && !hasOwner && (
                <Claim companySlug={company.slug}>
                  This is a community-generated profile. To edit this page{' '}
                  <strong>
                    <a
                      href={`/become-company-member?company=${company.slug}`}
                      title={`Claim ${company.name}`}
                    >
                      claim this profile
                    </a>
                  </strong>
                </Claim>
              )}
              {currentUser && currentUserIsOwner && !isUserImpersonated && (
                <ComposerProvider debug privateMode={privateMode}>
                  <Composer />
                </ComposerProvider>
              )}
              <Sections
                isSiteAdmin={currentUser && currentUser.amIAdmin}
                isAdmin={isAdmin}
                company={company}
                isPrivate={isPrivate}
              />
            </Main>
          </Grid>
          {signin ? (
            <SigninDesktopModal redirect={company.path} onDismiss={exitModal} />
          ) : decision ? (
            <Query
              query={decisionQuery}
              variables={{id: decision.id}}
              fetchPolicy="network-only"
              ssr={false}
            >
              {({data: {stackDecision}}) => (
                <DecisionModal stackDecision={stackDecision} onDismiss={exitModal} />
              )}
            </Query>
          ) : null}
          {showPlan && (canEdit || currentUserIsOwner) && (
            <PlanModal success={planSuccess} onDismiss={exitModal} company={company} />
          )}
          {showPssForm && (
            <StackShareEnterpriseForm
              onDismiss={() => setShowPssForm(false)}
              title="Tell us about yourself"
              type="lead"
            />
          )}
        </Page>
      </>
    );
  }
  return null;
};

CompanyProfile.propTypes = {
  company: PropTypes.object,
  loading: PropTypes.bool,
  canEdit: PropTypes.bool,
  hasOwner: PropTypes.bool
};

export default CompanyProfile;
